import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  Filter,
  Loader2,
  Search,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import activityLogsApi from "../../api/activityLogsApi";
import { getApiErrorMessage } from "../../api/axiosClient";
import { useAlerts } from "../../components/feedback/AlertProvider";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import "../../styles/activity/manager-activity-logs.css";

const DEFAULT_ESTABLISHMENT_ID = 1;
const ESTABLISHMENT_STORAGE_KEY = "selected_establishment_id";

const moduleKeys = [
  "all",
  "orders",
  "products",
  "categories",
  "supplements",
  "menu_templates",
  "menu_settings",
  "menu_design",
  "account",
  "general",
];

const actionKeys = [
  "all",
  "created",
  "updated",
  "deleted",
  "status_updated",
  "availability_updated",
];

const replaceParams = (text, params = {}) => {
  if (typeof text !== "string") return text;

  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replaceAll(`{{${key}}}`, String(value ?? ""));
  }, text);
};

const translateFallback = (t, key, fallback, params = {}) => {
  const value = t(key, params);

  if (value === key) {
    return replaceParams(fallback, params);
  }

  return value;
};

const normalizeRole = (role) => {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
};

const formatDateTime = (value, language) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(language || "fr", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const getInitials = (name = "") => {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return "U";

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
};

const safeParseJson = (value) => {
  if (!value) return {};

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getActivityMetadata = (activity) => {
  return safeParseJson(activity?.metadata);
};

const getActivityUserId = (activity) => {
  const metadata = getActivityMetadata(activity);

  return (
    activity?.user_id ||
    activity?.user?.id ||
    metadata.user_id ||
    metadata.actor_id ||
    null
  );
};

const getLoggedUserId = (user) => {
  return (
    user?.id ||
    user?.user_id ||
    user?.profile?.id ||
    user?.data?.id ||
    null
  );
};

const isSameId = (left, right) => {
  if (
    left === null ||
    left === undefined ||
    right === null ||
    right === undefined
  ) {
    return false;
  }

  return String(left) === String(right);
};

const getActivityProfilePath = (activity, user) => {
  const activityUserId = getActivityUserId(activity);

  if (!activityUserId) {
    return null;
  }

  if (isSameId(activityUserId, getLoggedUserId(user))) {
    return "/account-settings";
  }

  return `/users/${activityUserId}`;
};

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const getFirstId = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    return value[0]?.id || value[0]?.establishment_id || null;
  }

  if (Array.isArray(value?.data)) {
    return value.data[0]?.id || value.data[0]?.establishment_id || null;
  }

  return value?.id || value?.establishment_id || null;
};

const getStoredEstablishmentId = () => {
  const storageKeys = [
    ESTABLISHMENT_STORAGE_KEY,
    "selectedEstablishmentId",
    "current_establishment_id",
    "currentEstablishmentId",
    "active_establishment_id",
    "activeEstablishmentId",
    "establishment_id",
  ];

  for (const key of storageKeys) {
    const storedValue = localStorage.getItem(key);

    if (storedValue) {
      return storedValue;
    }
  }

  return null;
};

const getUserEstablishmentId = (user) => {
  return (
    user?.establishment_id ||
    user?.establishment?.id ||
    user?.restaurant?.id ||
    user?.managed_establishment_id ||
    user?.managedEstablishment?.id ||
    getFirstId(user?.establishments) ||
    getFirstId(user?.managed_establishments) ||
    getFirstId(user?.managedEstablishments) ||
    getFirstId(user?.restaurants) ||
    getFirstId(user?.business?.establishments) ||
    getFirstId(user?.businesses?.[0]?.establishments) ||
    getStoredEstablishmentId() ||
    null
  );
};

const getStatusLabel = (t, status) => {
  if (!status) return "-";

  return translateFallback(
    t,
    `orders.status.${status}`,
    status,
  );
};

const getModuleLabel = (t, module) => {
  return translateFallback(
    t,
    `activityTracking.modules.${module}`,
    module || "-",
  );
};

const getActionLabel = (t, action) => {
  return translateFallback(
    t,
    `activityTracking.actions.${action}`,
    action || "-",
  );
};

const getActorName = (t, activity) => {
  if (activity.user_name) {
    return activity.user_name;
  }

  if (normalizeRole(activity.user_role) === "customer") {
    return translateFallback(
      t,
      "activityTracking.actors.customer",
      "Customer",
    );
  }

  return translateFallback(
    t,
    "activityTracking.actors.unknown",
    "Someone",
  );
};

const getActorRoleLabel = (t, role) => {
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === "customer") {
    return translateFallback(
      t,
      "activityTracking.actors.customer",
      "Customer",
    );
  }

  const roleKeyMap = {
    superadmin: "super_admin",
    manager: "manager",
    waiter: "waiter",
    serveur: "waiter",
    cuisini: "cuisini",
    cuisine: "cuisini",
  };

  const translationKey = roleKeyMap[normalizedRole];

  if (translationKey) {
    return translateFallback(
      t,
      `usersManagement.roles.${translationKey}`,
      role || "-",
    );
  }

  return role || "-";
};

const getOrderNumber = (activity) => {
  const metadata = getActivityMetadata(activity);

  return (
    metadata.order_number ||
    activity.order_number ||
    (activity.subject_id ? `#${activity.subject_id}` : "-")
  );
};

const getActivityDescription = (t, activity) => {
  const metadata = getActivityMetadata(activity);
  const actor = getActorName(t, activity);
  const module = getModuleLabel(t, activity.module);
  const action = getActionLabel(t, activity.action);
  const order = getOrderNumber(activity);

  if (
    activity.module === "orders" &&
    activity.action === "status_updated"
  ) {
    return translateFallback(
      t,
      "activityTracking.descriptions.orderStatusUpdated",
      "{{actor}} changed order {{order}} status from {{from}} to {{to}}.",
      {
        actor,
        order,
        from: getStatusLabel(t, metadata.from_status),
        to: getStatusLabel(t, metadata.to_status),
      },
    );
  }

  if (
    activity.module === "orders" &&
    activity.action === "created" &&
    normalizeRole(activity.user_role) === "customer"
  ) {
    return translateFallback(
      t,
      "activityTracking.descriptions.customerPlacedOrder",
      "{{actor}} placed order {{order}}.",
      {
        actor,
        order,
      },
    );
  }

  if (
    activity.module === "orders" &&
    activity.action === "created"
  ) {
    return translateFallback(
      t,
      "activityTracking.descriptions.staffCreatedOrder",
      "{{actor}} created order {{order}}.",
      {
        actor,
        order,
      },
    );
  }

  return translateFallback(
    t,
    "activityTracking.descriptions.generic",
    "{{actor}} performed “{{action}}” in {{module}}.",
    {
      actor,
      action,
      module,
    },
  );
};

const getActivitySubDescription = (t, activity) => {
  const metadata = getActivityMetadata(activity);

  if (
    activity.module === "orders" &&
    activity.action === "status_updated"
  ) {
    return translateFallback(
      t,
      "activityTracking.details.statusChange",
      "{{from}} → {{to}}",
      {
        from: getStatusLabel(t, metadata.from_status),
        to: getStatusLabel(t, metadata.to_status),
      },
    );
  }

  if (
    activity.module === "orders" &&
    metadata.total_amount
  ) {
    return translateFallback(
      t,
      "activityTracking.details.amount",
      "Amount: {{amount}} {{currency}}",
      {
        amount: metadata.total_amount,
        currency: metadata.currency || "",
      },
    );
  }

  if (metadata.order_number) {
    return translateFallback(
      t,
      "activityTracking.details.orderNumber",
      "Order: {{order}}",
      {
        order: metadata.order_number,
      },
    );
  }

  return activity.subject_id
    ? translateFallback(
        t,
        "activityTracking.details.record",
        "Record #{{id}}",
        {
          id: activity.subject_id,
        },
      )
    : "";
};

function FilterDropdown({
  value,
  options,
  labelForOption,
  onChange,
  ariaLabel,
}) {
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedLabel = labelForOption(value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, []);

  return (
    <div
      className={`manager-activity-select ${
        open
          ? "manager-activity-select--open"
          : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="manager-activity-select__trigger"
        onClick={() =>
          setOpen((currentValue) => !currentValue)
        }
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>{selectedLabel}</span>

        <ChevronDown size={16} />
      </button>

      {open && (
        <div
          className="manager-activity-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const active = option === value;

            return (
              <button
                key={option}
                type="button"
                className={`manager-activity-select__option ${
                  active
                    ? "manager-activity-select__option--active"
                    : ""
                }`}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                role="option"
                aria-selected={active}
              >
                <span>
                  {labelForOption(option)}
                </span>

                {active && (
                  <Check size={14} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CalendarSelect({
  value,
  options,
  onChange,
  ariaLabel,
  compact = false,
}) {
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) === String(value),
    ) || options[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, []);

  return (
    <div
      className={`manager-activity-calendar-select ${
        open
          ? "manager-activity-calendar-select--open"
          : ""
      } ${
        compact
          ? "manager-activity-calendar-select--compact"
          : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="manager-activity-calendar-select__trigger"
        onClick={() =>
          setOpen((currentValue) => !currentValue)
        }
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>{selectedOption?.label}</span>

        <ChevronDown size={14} />
      </button>

      {open && (
        <div
          className="manager-activity-calendar-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const active =
              String(option.value) === String(value);

            return (
              <button
                key={option.value}
                type="button"
                className={`manager-activity-calendar-select__option ${
                  active
                    ? "manager-activity-calendar-select__option--active"
                    : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={active}
              >
                <span>{option.label}</span>

                {active && (
                  <Check size={13} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const toDateInputValue = (date) => {
  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0");

  const day = String(date.getDate()).padStart(
    2,
    "0",
  );

  return `${year}-${month}-${day}`;
};

const parseDateInputValue = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value)
    .split("-")
    .map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const addMonths = (date, amount) => {
  return new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    1,
  );
};

const isSameDay = (leftDate, rightDate) => {
  if (!leftDate || !rightDate) {
    return false;
  }

  return (
    toDateInputValue(leftDate) ===
    toDateInputValue(rightDate)
  );
};

const getCalendarDays = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(
    year,
    month,
    1,
  );

  const firstDayIndex =
    (firstDay.getDay() + 6) % 7;

  const startDate = new Date(
    year,
    month,
    1 - firstDayIndex,
  );

  return Array.from(
    { length: 42 },
    (_, index) => {
      const date = new Date(startDate);

      date.setDate(
        startDate.getDate() + index,
      );

      return date;
    },
  );
};

const formatDisplayDate = (
  value,
  language,
) => {
  const parsedDate =
    parseDateInputValue(value);

  if (!parsedDate) return "";

  try {
    return new Intl.DateTimeFormat(
      language || "fr",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      },
    ).format(parsedDate);
  } catch {
    return value;
  }
};

const getMonthOptions = (language) => {
  return Array.from(
    { length: 12 },
    (_, monthIndex) => ({
      value: monthIndex,
      label: new Intl.DateTimeFormat(
        language || "fr",
        {
          month: "long",
        },
      ).format(
        new Date(2026, monthIndex, 1),
      ),
    }),
  );
};

const getYearOptions = (viewDate) => {
  const currentYear =
    new Date().getFullYear();

  const selectedYear =
    viewDate.getFullYear();

  const startYear = Math.min(
    currentYear - 6,
    selectedYear - 6,
  );

  const endYear = Math.max(
    currentYear + 6,
    selectedYear + 6,
  );

  return Array.from(
    {
      length:
        endYear - startYear + 1,
    },
    (_, index) => startYear + index,
  );
};

function DatePickerDropdown({
  value,
  onChange,
  placeholder,
  ariaLabel,
  language,
  t,
  isRtl,
}) {
  const dropdownRef = useRef(null);

  const selectedDate =
    parseDateInputValue(value);

  const [open, setOpen] =
    useState(false);

  const [viewDate, setViewDate] =
    useState(
      selectedDate || new Date(),
    );

  useEffect(() => {
    if (selectedDate) {
      setViewDate(
        new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          1,
        ),
      );
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, []);

  const calendarDays =
    getCalendarDays(viewDate);

  const today = new Date();

  const monthOptions =
    getMonthOptions(language);

  const yearOptions =
    getYearOptions(viewDate);

  const weekDays = Array.from(
    { length: 7 },
    (_, index) => {
      const date = new Date(
        2026,
        0,
        5 + index,
      );

      return new Intl.DateTimeFormat(
        language || "fr",
        {
          weekday: "short",
        },
      ).format(date);
    },
  );

  const selectDate = (date) => {
    onChange(toDateInputValue(date));
    setOpen(false);
  };

  const changeMonth = (month) => {
    setViewDate(
      new Date(
        viewDate.getFullYear(),
        Number(month),
        1,
      ),
    );
  };

  const changeYear = (year) => {
    setViewDate(
      new Date(
        Number(year),
        viewDate.getMonth(),
        1,
      ),
    );
  };

  return (
    <div
      className={`manager-activity-date ${
        open
          ? "manager-activity-date--open"
          : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={`manager-activity-date__trigger ${
          value ? "has-value" : ""
        }`}
        onClick={() =>
          setOpen((currentValue) => !currentValue)
        }
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <CalendarDays size={16} />

        <span>
          {value
            ? formatDisplayDate(
                value,
                language,
              )
            : placeholder}
        </span>

        {value ? (
          <span
            className="manager-activity-date__clear"
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onChange("");
            }}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" ||
                event.key === " "
              ) {
                event.preventDefault();
                event.stopPropagation();
                onChange("");
              }
            }}
            aria-label={translateFallback(
              t,
              "activityTracking.calendar.clearDate",
              "Clear date",
            )}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={16} />
        )}
      </button>

      {open && (
        <div className="manager-activity-date__menu">
          <div className="manager-activity-date__header">
            <button
              type="button"
              className="manager-activity-date__nav"
              onClick={() =>
                setViewDate(
                  (currentDate) =>
                    addMonths(
                      currentDate,
                      -1,
                    ),
                )
              }
              aria-label={translateFallback(
                t,
                "activityTracking.calendar.previousMonth",
                "Previous month",
              )}
            >
              {isRtl ? (
                <ChevronRight size={15} />
              ) : (
                <ChevronLeft size={15} />
              )}
            </button>

            <div className="manager-activity-date__selects">
              <CalendarSelect
                value={viewDate.getMonth()}
                options={monthOptions}
                onChange={changeMonth}
                ariaLabel={translateFallback(
                  t,
                  "activityTracking.calendar.month",
                  "Month",
                )}
              />

              <CalendarSelect
                value={viewDate.getFullYear()}
                options={yearOptions.map(
                  (year) => ({
                    value: year,
                    label: String(year),
                  }),
                )}
                onChange={changeYear}
                ariaLabel={translateFallback(
                  t,
                  "activityTracking.calendar.year",
                  "Year",
                )}
                compact
              />
            </div>

            <button
              type="button"
              className="manager-activity-date__nav"
              onClick={() =>
                setViewDate(
                  (currentDate) =>
                    addMonths(
                      currentDate,
                      1,
                    ),
                )
              }
              aria-label={translateFallback(
                t,
                "activityTracking.calendar.nextMonth",
                "Next month",
              )}
            >
              {isRtl ? (
                <ChevronLeft size={15} />
              ) : (
                <ChevronRight size={15} />
              )}
            </button>
          </div>

          <div className="manager-activity-date__weekdays">
            {weekDays.map((weekDay) => (
              <span key={weekDay}>
                {weekDay.replace(".", "")}
              </span>
            ))}
          </div>

          <div className="manager-activity-date__grid">
            {calendarDays.map((date) => {
              const outsideMonth =
                date.getMonth() !==
                viewDate.getMonth();

              const selected =
                isSameDay(
                  date,
                  selectedDate,
                );

              const current =
                isSameDay(date, today);

              return (
                <button
                  key={toDateInputValue(
                    date,
                  )}
                  type="button"
                  className={[
                    outsideMonth
                      ? "is-muted"
                      : "",
                    selected
                      ? "is-selected"
                      : "",
                    current
                      ? "is-today"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() =>
                    selectDate(date)
                  }
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ManagerActivityLogs() {
  const { t, language } =
    useLanguage();

  const { showAlert } =
    useAlerts();

  const { user } = useAuth();

  const [direction, setDirection] =
    useState(
      document.documentElement.dir ||
        "ltr",
    );

  const isRtl =
    direction === "rtl";

  const establishmentId = useMemo(() => {
    const resolvedId =
      getUserEstablishmentId(user) ||
      DEFAULT_ESTABLISHMENT_ID;

    if (resolvedId) {
      localStorage.setItem(
        ESTABLISHMENT_STORAGE_KEY,
        String(resolvedId),
      );
    }

    return resolvedId;
  }, [user]);

  const [activities, setActivities] =
    useState([]);

  const [summary, setSummary] =
    useState(null);

  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  const [filters, setFilters] =
    useState({
      search: "",
      module: "all",
      action: "all",
      date_from: "",
      date_to: "",
      page: 1,
      per_page: 20,
    });

  const [loading, setLoading] =
    useState(true);

  const [
    summaryLoading,
    setSummaryLoading,
  ] = useState(true);

  const [
    activityError,
    setActivityError,
  ] = useState("");

  const [
    selectedActivity,
    setSelectedActivity,
  ] = useState(null);

  // Keep this page synchronized with the global language direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          "ltr",
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  const selectedModuleLabel =
    useMemo(() => {
      return getModuleLabel(
        t,
        filters.module,
      );
    }, [filters.module, t]);

  const selectedActivityMetadata =
    useMemo(() => {
      return getActivityMetadata(
        selectedActivity,
      );
    }, [selectedActivity]);

  const selectedActivityProfilePath =
    useMemo(() => {
      return selectedActivity
        ? getActivityProfilePath(
            selectedActivity,
            user,
          )
        : null;
    }, [selectedActivity, user]);

  const updateFilter = (
    field,
    value,
  ) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
      page:
        field === "page"
          ? value
          : 1,
    }));
  };

  const loadActivities = async () => {
    if (!establishmentId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setActivityError("");

      const params = {
        search:
          filters.search ||
          undefined,
        module:
          filters.module === "all"
            ? undefined
            : filters.module,
        action:
          filters.action === "all"
            ? undefined
            : filters.action,
        date_from:
          filters.date_from ||
          undefined,
        date_to:
          filters.date_to ||
          undefined,
        page: filters.page,
        per_page: filters.per_page,
      };

      const response =
        await activityLogsApi.getActivityLogs(
          establishmentId,
          params,
        );

      setActivities(
        response.data?.data || [],
      );

      setMeta(
        response.data?.meta || {
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: 0,
        },
      );
    } catch (error) {
      const message =
        getApiErrorMessage(error);

      setActivityError(message);

      showAlert({
        type: "error",
        title: t("alerts.error"),
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!establishmentId) {
      return;
    }

    try {
      setSummaryLoading(true);

      const response =
        await activityLogsApi.getActivitySummary(
          establishmentId,
        );

      setSummary(
        response.data?.data || null,
      );
    } catch (error) {
      showAlert({
        type: "error",
        title: t("alerts.error"),
        message:
          getApiErrorMessage(error),
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishmentId]);

  useEffect(() => {
    loadActivities();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    establishmentId,
    filters.page,
    filters.per_page,
    filters.module,
    filters.action,
    filters.date_from,
    filters.date_to,
  ]);

  const handleSearchSubmit = (
    event,
  ) => {
    event.preventDefault();
    loadActivities();
  };

  const canGoPrevious =
    meta.current_page > 1;

  const canGoNext =
    meta.current_page <
    meta.last_page;

  return (
    <main
      className={`manager-activity ${
        isRtl
          ? "manager-activity--rtl"
          : "manager-activity--ltr"
      }`}
      dir={direction}
    >
      <section className="manager-activity__hero">
        <div>
          <p>
            <Activity size={16} />

            {translateFallback(
              t,
              "activityTracking.eyebrow",
              "Manager activity",
            )}
          </p>

          <h1>
            {translateFallback(
              t,
              "activityTracking.title",
              "Activity tracking",
            )}
          </h1>

          <span>
            {translateFallback(
              t,
              "activityTracking.description",
              "Follow staff actions, customer orders, order changes and important dashboard updates for this business.",
            )}
          </span>
        </div>

        <div className="manager-activity__hero-badge">
          <ShieldCheck size={18} />

          {translateFallback(
            t,
            "activityTracking.businessOnly",
            "Business activity",
          )}
        </div>
      </section>

      <section className="manager-activity__stats">
        <article>
          <span>
            <CalendarDays size={19} />
          </span>

          <div>
            <p>
              {translateFallback(
                t,
                "activityTracking.stats.today",
                "Today",
              )}
            </p>

            <strong>
              {summaryLoading
                ? "-"
                : summary?.today_count ||
                  0}
            </strong>
          </div>
        </article>

        <article>
          <span>
            <ClipboardList size={19} />
          </span>

          <div>
            <p>
              {translateFallback(
                t,
                "activityTracking.stats.week",
                "Last 7 days",
              )}
            </p>

            <strong>
              {summaryLoading
                ? "-"
                : summary?.week_count ||
                  0}
            </strong>
          </div>
        </article>

        <article>
          <span>
            <Filter size={19} />
          </span>

          <div>
            <p>
              {translateFallback(
                t,
                "activityTracking.stats.currentModule",
                "Current module",
              )}
            </p>

            <strong>
              {selectedModuleLabel}
            </strong>
          </div>
        </article>
      </section>

      <section className="manager-activity__panel">
        <form
          className="manager-activity__filters"
          onSubmit={
            handleSearchSubmit
          }
        >
          <label className="manager-activity__search">
            <Search size={18} />

            <input
              type="search"
              value={filters.search}
              onChange={(event) =>
                updateFilter(
                  "search",
                  event.target.value,
                )
              }
              placeholder={translateFallback(
                t,
                "activityTracking.filters.searchPlaceholder",
                "Search user, action, module, order...",
              )}
            />
          </label>

          <FilterDropdown
            value={filters.module}
            options={moduleKeys}
            labelForOption={(
              moduleKey,
            ) =>
              getModuleLabel(
                t,
                moduleKey,
              )
            }
            onChange={(moduleKey) =>
              updateFilter(
                "module",
                moduleKey,
              )
            }
            ariaLabel={translateFallback(
              t,
              "activityTracking.table.module",
              "Module",
            )}
          />

          <FilterDropdown
            value={filters.action}
            options={actionKeys}
            labelForOption={(
              actionKey,
            ) =>
              getActionLabel(
                t,
                actionKey,
              )
            }
            onChange={(actionKey) =>
              updateFilter(
                "action",
                actionKey,
              )
            }
            ariaLabel={translateFallback(
              t,
              "activityTracking.table.action",
              "Action",
            )}
          />

          <DatePickerDropdown
            value={filters.date_from}
            onChange={(value) =>
              updateFilter(
                "date_from",
                value,
              )
            }
            placeholder={translateFallback(
              t,
              "activityTracking.filters.dateFrom",
              "From date",
            )}
            ariaLabel={translateFallback(
              t,
              "activityTracking.filters.dateFrom",
              "From date",
            )}
            language={language}
            t={t}
            isRtl={isRtl}
          />

          <DatePickerDropdown
            value={filters.date_to}
            onChange={(value) =>
              updateFilter(
                "date_to",
                value,
              )
            }
            placeholder={translateFallback(
              t,
              "activityTracking.filters.dateTo",
              "To date",
            )}
            ariaLabel={translateFallback(
              t,
              "activityTracking.filters.dateTo",
              "To date",
            )}
            language={language}
            t={t}
            isRtl={isRtl}
          />

          <button type="submit">
            <Search size={16} />

            {translateFallback(
              t,
              "activityTracking.filters.apply",
              "Apply",
            )}
          </button>
        </form>

        <div className="manager-activity__table-wrap">
          <table className="manager-activity__table">
            <thead>
              <tr>
                <th>
                  {translateFallback(
                    t,
                    "activityTracking.table.user",
                    "User",
                  )}
                </th>

                <th>
                  {translateFallback(
                    t,
                    "activityTracking.table.module",
                    "Module",
                  )}
                </th>

                <th>
                  {translateFallback(
                    t,
                    "activityTracking.table.action",
                    "Action",
                  )}
                </th>

                <th>
                  {translateFallback(
                    t,
                    "activityTracking.table.date",
                    "Date",
                  )}
                </th>

                <th>
                  {translateFallback(
                    t,
                    "activityTracking.table.actions",
                    "Actions",
                  )}
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5">
                    <div className="manager-activity__loading">
                      <Loader2 size={22} />

                      {translateFallback(
                        t,
                        "activityTracking.loading",
                        "Loading activity logs...",
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                activities.map(
                  (activity) => (
                    <tr key={activity.id}>
                      <td>
                        <div className="manager-activity-user">
                          <span>
                            {getInitials(
                              activity.user_name ||
                                activity.user_role,
                            )}
                          </span>

                          <div>
                            <strong>
                              {getActorName(
                                t,
                                activity,
                              )}
                            </strong>

                            <small>
                              {getActorRoleLabel(
                                t,
                                activity.user_role,
                              )}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="manager-activity-pill manager-activity-pill--module">
                          {getModuleLabel(
                            t,
                            activity.module,
                          )}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`manager-activity-pill manager-activity-pill--${activity.action}`}
                        >
                          {getActionLabel(
                            t,
                            activity.action,
                          )}
                        </span>
                      </td>

                      <td>
                        {formatDateTime(
                          activity.created_at,
                          language,
                        )}
                      </td>

                      <td>
                        <div className="manager-activity-row-actions">
                          {getActivityProfilePath(
                            activity,
                            user,
                          ) ? (
                            <Link
                              to={getActivityProfilePath(
                                activity,
                                user,
                              )}
                              className="manager-activity-action-button"
                              title={translateFallback(
                                t,
                                "activityTracking.actions.viewUserProfile",
                                "View user profile",
                              )}
                            >
                              <UserRound size={15} />

                              <span>
                                {translateFallback(
                                  t,
                                  "activityTracking.actions.profile",
                                  "Profile",
                                )}
                              </span>
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="manager-activity-action-button"
                              disabled
                              title={translateFallback(
                                t,
                                "activityTracking.actions.noUserProfile",
                                "No user profile available",
                              )}
                            >
                              <UserRound size={15} />

                              <span>
                                {translateFallback(
                                  t,
                                  "activityTracking.actions.profile",
                                  "Profile",
                                )}
                              </span>
                            </button>
                          )}

                          <button
                            type="button"
                            className="manager-activity-action-button"
                            onClick={() =>
                              setSelectedActivity(
                                activity,
                              )
                            }
                          >
                            <Eye size={15} />

                            <span>
                              {translateFallback(
                                t,
                                "activityTracking.actions.details",
                                "Details",
                              )}
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                )
              )}

              {!loading &&
                activityError && (
                  <tr>
                    <td colSpan="5">
                      <div className="manager-activity__empty manager-activity__empty--error">
                        <Activity size={24} />

                        {activityError}
                      </div>
                    </td>
                  </tr>
                )}

              {!loading &&
                !activityError &&
                !activities.length && (
                  <tr>
                    <td colSpan="5">
                      <div className="manager-activity__empty">
                        <Activity size={24} />

                        {translateFallback(
                          t,
                          "activityTracking.empty",
                          "No activity found for the selected filters.",
                        )}
                      </div>
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>

        <footer className="manager-activity__pagination">
          <span>
            {translateFallback(
              t,
              "activityTracking.pagination.total",
              "Total",
            )}
            : {meta.total}
          </span>

          <div>
            <button
              type="button"
              disabled={
                !canGoPrevious
              }
              onClick={() =>
                updateFilter(
                  "page",
                  meta.current_page -
                    1,
                )
              }
              aria-label={translateFallback(
                t,
                "activityTracking.pagination.previous",
                "Previous page",
              )}
            >
              {isRtl ? (
                <ChevronRight size={16} />
              ) : (
                <ChevronLeft size={16} />
              )}
            </button>

            <strong>
              {meta.current_page} /{" "}
              {meta.last_page}
            </strong>

            <button
              type="button"
              disabled={!canGoNext}
              onClick={() =>
                updateFilter(
                  "page",
                  meta.current_page +
                    1,
                )
              }
              aria-label={translateFallback(
                t,
                "activityTracking.pagination.next",
                "Next page",
              )}
            >
              {isRtl ? (
                <ChevronLeft size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </footer>
      </section>

      {selectedActivity && (
        <div
          className="manager-activity-modal-backdrop"
          role="presentation"
          onMouseDown={() =>
            setSelectedActivity(null)
          }
        >
          <section
            className="manager-activity-modal"
            role="dialog"
            aria-modal="true"
            aria-label={translateFallback(
              t,
              "activityTracking.detailsModal.title",
              "Activity details",
            )}
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            <header className="manager-activity-modal__header">
              <div>
                <span>
                  {getModuleLabel(
                    t,
                    selectedActivity.module,
                  )}
                </span>

                <h2>
                  {translateFallback(
                    t,
                    "activityTracking.detailsModal.title",
                    "Activity details",
                  )}
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedActivity(
                    null,
                  )
                }
                aria-label={translateFallback(
                  t,
                  "common.close",
                  "Close",
                )}
              >
                <X size={18} />
              </button>
            </header>

            <div className="manager-activity-modal__summary">
              <div className="manager-activity-user">
                <span>
                  {getInitials(
                    selectedActivity.user_name ||
                      selectedActivity.user_role,
                  )}
                </span>

                <div>
                  <strong>
                    {getActorName(
                      t,
                      selectedActivity,
                    )}
                  </strong>

                  <small>
                    {getActorRoleLabel(
                      t,
                      selectedActivity.user_role,
                    )}
                  </small>
                </div>
              </div>

              {selectedActivityProfilePath ? (
                <Link
                  to={
                    selectedActivityProfilePath
                  }
                  className="manager-activity-action-button"
                >
                  <UserRound size={15} />

                  <span>
                    {translateFallback(
                      t,
                      "activityTracking.actions.profile",
                      "Profile",
                    )}
                  </span>
                </Link>
              ) : null}
            </div>

            <div className="manager-activity-modal__grid">
              <article>
                <span>
                  {translateFallback(
                    t,
                    "activityTracking.table.action",
                    "Action",
                  )}
                </span>

                <strong>
                  {getActionLabel(
                    t,
                    selectedActivity.action,
                  )}
                </strong>
              </article>

              <article>
                <span>
                  {translateFallback(
                    t,
                    "activityTracking.table.module",
                    "Module",
                  )}
                </span>

                <strong>
                  {getModuleLabel(
                    t,
                    selectedActivity.module,
                  )}
                </strong>
              </article>

              <article>
                <span>
                  {translateFallback(
                    t,
                    "activityTracking.table.date",
                    "Date",
                  )}
                </span>

                <strong>
                  {formatDateTime(
                    selectedActivity.created_at,
                    language,
                  )}
                </strong>
              </article>

              <article>
                <span>
                  {translateFallback(
                    t,
                    "activityTracking.details.recordLabel",
                    "Record",
                  )}
                </span>

                <strong>
                  {selectedActivity.subject_id
                    ? `#${selectedActivity.subject_id}`
                    : "-"}
                </strong>
              </article>
            </div>

            <div className="manager-activity-modal__description">
              <span>
                {translateFallback(
                  t,
                  "activityTracking.table.description",
                  "Description",
                )}
              </span>

              <strong>
                {getActivityDescription(
                  t,
                  selectedActivity,
                )}
              </strong>

              {getActivitySubDescription(
                t,
                selectedActivity,
              ) ? (
                <small>
                  {getActivitySubDescription(
                    t,
                    selectedActivity,
                  )}
                </small>
              ) : null}
            </div>

            <div className="manager-activity-modal__details">
              <h3>
                {translateFallback(
                  t,
                  "activityTracking.detailsModal.rawDetails",
                  "Full details",
                )}
              </h3>

              <dl>
                <div>
                  <dt>
                    {translateFallback(
                      t,
                      "activityTracking.detailsModal.id",
                      "ID",
                    )}
                  </dt>

                  <dd>
                    {formatDetailValue(
                      selectedActivity.id,
                    )}
                  </dd>
                </div>

                <div>
                  <dt>
                    {translateFallback(
                      t,
                      "activityTracking.detailsModal.subjectType",
                      "Subject type",
                    )}
                  </dt>

                  <dd>
                    {formatDetailValue(
                      selectedActivity.subject_type,
                    )}
                  </dd>
                </div>

                <div>
                  <dt>
                    {translateFallback(
                      t,
                      "activityTracking.detailsModal.subjectId",
                      "Subject ID",
                    )}
                  </dt>

                  <dd>
                    {formatDetailValue(
                      selectedActivity.subject_id,
                    )}
                  </dd>
                </div>

                <div>
                  <dt>
                    {translateFallback(
                      t,
                      "activityTracking.detailsModal.userId",
                      "User ID",
                    )}
                  </dt>

                  <dd>
                    {formatDetailValue(
                      getActivityUserId(
                        selectedActivity,
                      ),
                    )}
                  </dd>
                </div>
              </dl>

              {Object.keys(
                selectedActivityMetadata ||
                  {},
              ).length ? (
                <pre dir="ltr">
                  {JSON.stringify(
                    selectedActivityMetadata,
                    null,
                    2,
                  )}
                </pre>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default ManagerActivityLogs;